import React, { useState, useEffect, useCallback, useRef } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    doc,
    getDoc,
    setDoc,
    deleteDoc,
    serverTimestamp,
    collection,
    getDocs,
    addDoc,
    query,
    where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import GuidelinesModal from "../components/guidelines";
import "../css/App.css";
import "../css/guidelines.css";
import { SectionEditor, ReferencesEditor, ImageSlideEditor } from "./EditorComponents";
import { talkImageMode, slideImageMode, LAYOUT_MODES } from "../sectionLayout";
import { Helmet } from "@dr.pogodin/react-helmet";
// Generate a stable-ish unique ID for a section; used once per new section.
const createSectionId = () =>
    "sec_" +
    Date.now().toString(36) +
    "_" +
    Math.random().toString(36).slice(2, 8);



export default function EditorPage({ currentUser }) {
    const [isMobile, setIsMobile] = useState(false);
    const [isGuidelinesOpen, setIsGuidelinesOpen] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const { talkId } = useParams();
    const navigate = useNavigate();

    const [talk, setTalk] = useState({
        title: "",
        sections: [{ header: "", body: "", type: "content" }],
        status: "Draft",
        authorId: "",
        verseText: "",
        verseRef: "",
        references: "",
        referencesBodies: [],
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [targetStatus, setTargetStatus] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [currentUserName, setCurrentUserName] = useState(null);

    const [isDirty, setIsDirty] = useState(false);
    const initialLoadDone = useRef(false);
    const suppressDirtyRef = useRef(false);

    const [showSaveToast, setShowSaveToast] = useState(false);
    const saveToastTimer = useRef(null);
    const [commentModal, setCommentModal] = useState({
        open: false,
        sectionIndex: null,
        text: "",
    });
    const [sectionComments, setSectionComments] = useState({});

    // Ensure sections conform to schema (content, divider, image). Read legacy, write minimal fields only.
    const normalizeSections = (sections) =>
        (sections || []).map((s) => {
            const type = s?.type || "content";
            const header = s?.header || "";
            const sectionId = s?.sectionId || createSectionId();

            // IMAGE SECTION — minimal persisted shape with optional text
            if (type === "image") {
                const imageUrl = typeof s?.imageUrl === "string" ? s.imageUrl : "";
                const imageAlt = typeof s?.imageAlt === "string" ? s.imageAlt : "";
                const imageCaption = typeof s?.imageCaption === "string" ? s.imageCaption : "";
                const body = typeof s?.body === "string" ? s.body : "";
                const headerVal = typeof s?.header === "string" ? s.header : "";
                // Per-surface display: explicit if set, else derived from legacy content.
                const talkLayout = LAYOUT_MODES.includes(s?.talkLayout) ? s.talkLayout : talkImageMode(s);
                const slideLayout = LAYOUT_MODES.includes(s?.slideLayout) ? s.slideLayout : slideImageMode(s);
                const base = {
                    sectionId, type: "image", header: headerVal,
                    imageUrl, imageAlt, imageCaption, body, talkLayout, slideLayout,
                };
                if (typeof s?.slideBody === "string") base.slideBody = s.slideBody;
                return base;
            }

            // DIVIDER SECTION — only header + type
            if (type === "divider") {
                return { sectionId, header, type: "divider" };
            }

            // CONTENT SECTION (default)
            const body = s?.body || "";

            // Prefer new schema `bodies`, otherwise read legacy forms without persisting them
            let bodies = [];
            if (Array.isArray(s?.bodies)) {
                bodies = s.bodies;
            } else if (typeof s?.continuations === "string") {
                try {
                    const parsed = JSON.parse(s.continuations);
                    if (Array.isArray(parsed)) bodies = parsed;
                } catch (_) { /* ignore bad JSON */ }
            } else if (s?.continued || (typeof s?.body2 === "string" && s.body2.length > 0)) {
                bodies = [s.body2 || ""];
            }

            // slideBody: custom HTML for the slideshow; omitted when null (inherit from web content)
            const slideBody = typeof s?.slideBody === "string" ? s.slideBody : null;

            // slideBodies: additional slide continuation parts (only relevant when slideBody is set)
            let slideBodies = [];
            if (Array.isArray(s?.slideBodies)) {
                slideBodies = s.slideBodies.filter(p => typeof p === "string");
            }

            const base = { sectionId, header, type: "content", body, bodies };
            if (slideBody !== null) {
                base.slideBody = slideBody;
                if (slideBodies.length > 0) base.slideBodies = slideBodies;
            }
            return base;
        });

    // Section management
    const addSection = (afterIndex = null) => {
        const newSection = { header: "", body: "", type: "content" };
        setTalk(t => {
            const current = normalizeSections(t.sections);
            if (afterIndex === null || afterIndex >= current.length - 1) {
                return { ...t, sections: normalizeSections([...current, newSection]) };
            } else {
                const next = [...current];
                next.splice(afterIndex + 1, 0, newSection);
                return { ...t, sections: normalizeSections(next) };
            }
        });
    };
    const addDividerSection = (afterIndex = null) => {
        const newDivider = { header: "Section Title", type: "divider" };
        setTalk(t => {
            const current = normalizeSections(t.sections);
            if (afterIndex === null || afterIndex >= current.length - 1) {
                return { ...t, sections: normalizeSections([...current, newDivider]) };
            } else {
                const next = [...current];
                next.splice(afterIndex + 1, 0, newDivider);
                return { ...t, sections: normalizeSections(next) };
            }
        });
    };
    const addImageSection = (afterIndex = null) => {
        // Default: full notes + image on the talk page, image-only on the slideshow.
        const newImage = { type: "image", imageUrl: "", body: "", talkLayout: "both", slideLayout: "image" };
        setTalk(t => {
            const current = normalizeSections(t.sections);
            if (afterIndex === null || afterIndex >= current.length - 1) {
                return { ...t, sections: normalizeSections([...current, newImage]) };
            } else {
                const next = [...current];
                next.splice(afterIndex + 1, 0, newImage);
                return { ...t, sections: normalizeSections(next) };
            }
        });
    };
    const removeSection = idx => {
        setTalk(t => ({ ...t, sections: normalizeSections(t.sections.filter((_, i) => i !== idx)) }));
    };
    const moveSection = (from, to) => {
        setTalk(t => {
            const s = [...t.sections];
            const [m] = s.splice(from, 1);
            s.splice(to, 0, m);
            return { ...t, sections: normalizeSections(s) };
        });
    };
    const updateSection = (idx, field, value) => {
        setTalk(t => {
            const s = [...t.sections];
            s[idx] = { ...s[idx], [field]: value };
            return { ...t, sections: normalizeSections(s) };
        });
    };

    // Open the "add comment" modal for a specific section
    const handleAddCommentForSection = (sectionIndex) => {
        setCommentModal({
            open: true,
            sectionIndex,
            text: "",
        });
    };

    // Submit the comment from the modal into the standalone `comments` collection
    const handleSubmitComment = async () => {
        if (!commentModal.open || commentModal.sectionIndex == null) return;
        const text = (commentModal.text || "").trim();
        if (!text) return;

        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
            alert("You must be signed in to add comments.");
            return;
        }

        const sectionIndex = commentModal.sectionIndex;
        const section = talk.sections[sectionIndex];
        const sectionId = section?.sectionId || createSectionId();
        const userName =
            currentUserName ||
            user.displayName ||
            user.email ||
            "Anonymous";

        try {
            const docRef = await addDoc(collection(db, "comments"), {
                talkId: talkId || null,
                sectionId,
                sectionIndex,
                text,
                userId: user.uid,
                userName,
                createdAt: serverTimestamp(),
            });

            const localComment = {
                id: docRef.id,
                talkId: talkId || null,
                sectionId,
                sectionIndex,
                text,
                userId: user.uid,
                userName,
                createdAt: new Date().toISOString(),
            };

            setSectionComments((prev) => {
                const next = { ...prev };
                const key = String(sectionId);
                const existing = Array.isArray(next[key]) ? [...next[key]] : [];
                existing.push(localComment);
                next[key] = existing;
                return next;
            });

            setCommentModal({
                open: false,
                sectionIndex: null,
                text: "",
            });
        } catch (err) {
            console.error("Error adding comment:", err);
            alert("Sorry, we couldn't save your comment. Please try again.");
        }
    };

    // Delete a comment for a specific section from the standalone `comments` collection
    const handleDeleteComment = async (sectionKey, commentId) => {
        if (!commentId) return;
        const confirmed = window.confirm(
            "Delete this comment? This cannot be undone."
        );
        if (!confirmed) return;

        try {
            await deleteDoc(doc(db, "comments", commentId));

            setSectionComments((prev) => {
                const next = { ...prev };
                const key = String(sectionKey);
                const existing = Array.isArray(next[key]) ? next[key] : [];
                next[key] = existing.filter((c) => c.id !== commentId);
                return next;
            });
        } catch (err) {
            console.error("Error deleting comment:", err);
            alert("Sorry, we couldn't delete that comment. Please try again.");
        }
    };
    useEffect(() => {
        if (!talkId) return;
        const loadComments = async () => {
            try {
                const q = query(
                    collection(db, "comments"),
                    where("talkId", "==", talkId)
                );
                const snap = await getDocs(q);
                const bySection = {};
                snap.forEach((docSnap) => {
                    const data = docSnap.data();
                    const rawKey = data.sectionId ?? data.sectionIndex;

                    if (rawKey === undefined || rawKey === null) return;

                    const key = typeof rawKey === "number" ? String(rawKey) : String(rawKey);

                    if (!bySection[key]) bySection[key] = [];
                    bySection[key].push({
                        id: docSnap.id,
                        ...data,
                    });
                });
                setSectionComments(bySection);
            } catch (err) {
                console.error("Error loading comments:", err);
            }
        };
        loadComments();
    }, [talkId]);

    const handleCancelComment = () => {
        setCommentModal({
            open: false,
            sectionIndex: null,
            text: "",
        });
    };

    // Load talk from Firestore if editing
    const loadTalk = useCallback(async () => {
        if (!talkId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const snap = await getDoc(doc(db, "talks", talkId));
        if (snap.exists()) {
            const data = snap.data();
            setTalk({
                title: data.title || "",
                sections: normalizeSections(data.sections),
                status: data.status || "Draft",
                authorId: data.authorId || "",
                verseText: data.verseText || "",
                verseRef: data.verseRef || "",
                references: data.references || "",
                referencesBodies: Array.isArray(data.referencesBodies)
                    ? data.referencesBodies.filter((s) => typeof s === "string" && s.replace(/<[^>]*>/g, "").trim() !== "")
                    : [],
            });
        }
        setLoading(false);
        initialLoadDone.current = true;
    }, [talkId]);

    useEffect(() => {
        initialLoadDone.current = false;
        loadTalk();
    }, [loadTalk]);

    // Mark dirty whenever talk changes after the initial load
    useEffect(() => {
        if (!initialLoadDone.current) return;
        if (suppressDirtyRef.current) {
            suppressDirtyRef.current = false;
            return;
        }
        setIsDirty(true);
    }, [talk]);

    // Load userRole from Firestore on auth state change
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const userRef = doc(db, "users", user.uid);
                    const userSnap = await getDoc(userRef);
                    if (userSnap.exists()) {
                        const data = userSnap.data() || {};
                        setUserRole(data.role || null);
                        setCurrentUserName(
                            data.name ||
                            data.displayName ||
                            user.displayName ||
                            user.email ||
                            null
                        );
                    } else {
                        setUserRole(null);
                        setCurrentUserName(
                            user.displayName ||
                            user.email ||
                            null
                        );
                    }
                } catch (e) {
                    console.error("Error loading user role:", e);
                    setUserRole(null);
                    setCurrentUserName(
                        user.displayName ||
                        user.email ||
                        null
                    );
                }
            } else {
                setUserRole(null);
                setCurrentUserName(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // Load all users for author dropdown
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const snap = await getDocs(collection(db, "users"));

                // Only show NATIONAL users as selectable authors.
                // Exclude any users affiliated with a chapter/school.
                const isChapterAffiliated = (data) => {
                    if (!data) return false;
                    // Common shapes we've used elsewhere in SOLE:
                    const hasChapterId = typeof data.chapterId === "string" && data.chapterId.trim() !== "";
                    const hasSchoolId = typeof data.schoolId === "string" && data.schoolId.trim() !== "";
                    const hasChapter = typeof data.chapter === "string" && data.chapter.trim() !== "";
                    const hasSchool = typeof data.school === "string" && data.school.trim() !== "";
                    const hasChapters = Array.isArray(data.chapters) && data.chapters.length > 0;
                    const hasChapterIds = Array.isArray(data.chapterIds) && data.chapterIds.length > 0;
                    const hasAffiliations = Array.isArray(data.affiliations) && data.affiliations.length > 0;
                    const hasInvitedChapters = data.invitedChapters && typeof data.invitedChapters === "object" && Object.keys(data.invitedChapters).length > 0;
                    const hasChapterRoles = data.chapterRoles && typeof data.chapterRoles === "object" && Object.keys(data.chapterRoles).length > 0;
                    return (
                        hasChapterId ||
                        hasSchoolId ||
                        hasChapter ||
                        hasSchool ||
                        hasChapters ||
                        hasChapterIds ||
                        hasAffiliations ||
                        hasInvitedChapters ||
                        hasChapterRoles
                    );
                };

                const isExplicitNational = (data) => {
                    if (!data) return false;

                    // Primary distinction in our DB: national users have a national workflow role.
                    const role = String(data.role || "").toLowerCase().trim();
                    const isNationalRole = ["writer", "deacon", "publisher", "admin", "national_admin", "national"].includes(role);

                    // Backward-compatible flags (if present)
                    const hasNationalFlag = Boolean(
                        data.isNationalUser ||
                        data.isNational ||
                        data.nationalUser ||
                        data.national === true ||
                        data.scope === "national" ||
                        data.userType === "national" ||
                        data.level === "national"
                    );

                    return isNationalRole || hasNationalFlag;
                };

                const isExplicitChapterUser = (data) => {
                    if (!data) return false;
                    const role = String(data.role || "").toLowerCase().trim();
                    const isMemberRole = role === "member";
                    return Boolean(
                        isMemberRole ||
                        data.isChapterUser ||
                        data.chapterUser ||
                        data.scope === "chapter" ||
                        data.userType === "chapter" ||
                        data.level === "chapter"
                    );
                };

                const list = snap.docs
                    .map((d) => {
                        const data = d.data() || {};
                        const label = data.displayName || data.name || data.email || d.id;
                        return { id: d.id, label, data };
                    })
                    .filter((u) => {
                        // Hard exclude chapter-affiliated people (chapter leaders, chapter users, school accounts)
                        if (isChapterAffiliated(u.data) || isExplicitChapterUser(u.data)) return false;

                        // Only allow explicitly NATIONAL users (prevents duplicates where a chapter user shares the same name)
                        return isExplicitNational(u.data);
                    })
                    .map(({ id, label }) => ({ id, label }));

                // De-dupe by label (defensive). If two national accounts somehow share a display name,
                // keep the first occurrence.
                const seenLabels = new Set();
                const deduped = [];
                for (const u of list) {
                    const key = String(u.label || "").trim().toLowerCase();
                    if (!key) continue;
                    if (seenLabels.has(key)) continue;
                    seenLabels.add(key);
                    deduped.push(u);
                }

                deduped.sort((a, b) => a.label.localeCompare(b.label));
                setUsers(deduped);
            } catch (e) {
                console.error("Error loading users:", e);
                setUsers([]);
            } finally {
                setUsersLoading(false);
            }
        };
        fetchUsers();
    }, []);

    // Save or update talk in Firestore
    const saveTalk = useCallback(async (newStatus = talk.status) => {
        setSaving(true);

        // Determine Firestore ref
        let talkRef;
        if (talkId) {
            talkRef = doc(db, "talks", talkId);
        } else {
            talkRef = doc(collection(db, "talks")); // auto-generated ID
        }

        const serializedSections = normalizeSections(talk.sections);
        const payload = {
            ...talk,
            // Normalize references fields
            references: typeof talk.references === "string" ? talk.references : "",
            referencesBodies: Array.isArray(talk.referencesBodies)
                ? talk.referencesBodies.filter((s) => typeof s === "string" && s.trim() !== "")
                : [],
            sections: serializedSections,
            status: newStatus,
            updatedAt: serverTimestamp(),
        };
        await setDoc(
            talkRef,
            payload,
            { merge: true }
        );

        if (!talkId) {
            navigate(`/editor/${talkRef.id}`, { replace: true });
        }

        suppressDirtyRef.current = true;
        setTalk((t) => ({ ...t, status: newStatus }));
        setSaving(false);
        setIsDirty(false);
        // Show a temporary "Saved" toast
        try { if (saveToastTimer.current) clearTimeout(saveToastTimer.current); } catch (_) { }
        setShowSaveToast(true);
        saveToastTimer.current = setTimeout(() => setShowSaveToast(false), 2000);
    }, [talk, talkId, navigate]);

    // Keyboard shortcut: Cmd/Ctrl + S to save
    useEffect(() => {
        const onKeyDown = (e) => {
            const isS = String(e.key).toLowerCase() === 's';
            if ((e.metaKey || e.ctrlKey) && isS) {
                e.preventDefault();
                if (!saving) {
                    saveTalk();
                }
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [saveTalk, saving]);

    // Cleanup toast timer on unmount
    useEffect(() => {
        return () => {
            if (saveToastTimer.current) {
                clearTimeout(saveToastTimer.current);
            }
        };
    }, []);

    // Warn before tab close / refresh when there are unsaved changes
    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isDirty]);

    // Confirm before navigating away in-app when there are unsaved changes
    const confirmNav = (to) => {
        if (isDirty && !window.confirm("You have unsaved changes. Leave anyway?")) return;
        navigate(to);
    };
    const deleteTalk = async () => {
        if (!talkId) return;
        const confirmed = window.confirm(
            "Are you sure you want to delete this talk? This action cannot be undone."
        );
        if (!confirmed) return;
        try {
            await deleteDoc(doc(db, "talks", talkId));
            setIsDirty(false);
            navigate("/national");
        } catch (err) {
            console.error("Error deleting talk:", err);
            alert("Failed to delete talk. Please try again.");
        }
    };

    // Trigger Cloud Function for assets
    const generateAsset = async (type) => {
        try {
            const response = await fetch(
                `https://us-central1-sole-15b6f.cloudfunctions.net/generate${type}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ talkId }),
                }
            );
            if (!response.ok) {
                const errorText = await response.text();
                alert(`Error: ${errorText}`);
                return;
            }
            alert(`${type} generation started.`);
        } catch (err) {
            alert(`Network error: ${err.message}`);
        }
    };

    if (loading) return <p>Loading…</p>;

    if (isMobile) {
        return (
            <>
                <Navbar />
                <main className="subscribe-page dashboard-page" style={{ paddingTop: "1rem", textAlign: "center" }}>
                    <div className="subscribe-hero" style={{ padding: "2rem" }}>
                        <h2>It's best to write talks on a desktop or larger screen.</h2>
                        <p>Please switch to a desktop to write talks.</p>
                    </div>
                </main>
                <Footer />
            </>
        );
    }

    // Only compute role/status logic after userRole is loaded
    const normalizedRole = userRole?.toLowerCase();
    const normalizedStatus = talk.status.toLowerCase();

    // Define full ordered list of statuses
    const allStatuses = ["Draft", "Ready for Deacons", "Ready to Publish", "Published"];
    // Determine user’s max approval level
    let maxIndex = 0;
    if (normalizedRole === "writer") maxIndex = 1;
    else if (normalizedRole === "deacon") maxIndex = 2;
    else if (normalizedRole === "publisher") maxIndex = 3;
    // Build allowed statuses up to user’s level
    const allowedStatuses = allStatuses.slice(0, maxIndex + 1);
    // Initialize targetStatus if not set
    if (targetStatus === null) {
        setTargetStatus(talk.status);
    }

    return (
        <>
            <Helmet>
                <title>Editing "{talk.title}"</title>
            </Helmet>
            <Navbar />
            <main className="editor-page">
                <div className="editor-hero">
                    <h1>{talk.title || "Untitled Talk"} {!talkId && "(New)"}</h1>
                    <input
                        type="text"
                        placeholder="Talk title…"
                        value={talk.title}
                        onChange={(e) =>
                            setTalk((t) => ({ ...t, title: e.target.value }))
                        }
                        className="subscribe-input"
                    />


                    <div className="editor-toolbar">
                        {/* ROW 1: Navigation left; Save/Delete grouped right */}
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                            {talkId && (
                                <button type="button" className="slideshow-button" onClick={() => confirmNav(`/slideshow/${talkId}`)}>
                                    View Slideshow
                                </button>
                            )}
                            <button type="button" className="slideshow-button" onClick={() => confirmNav(`/talk/${talkId}`)}>
                                View Talk Page
                            </button>
                            <button type="button" className="slideshow-button" onClick={() => confirmNav("/national")}>
                                Back to National
                            </button>
                            <button
                                type="button"
                                className="slideshow-button"
                                onClick={() => setIsGuidelinesOpen(true)}
                                title="View citation & formatting rules"
                            >
                                Guidelines
                            </button>
                            {/* Right-aligned actions */}
                            <div className="push-right" style={{ display: "inline-flex", gap: "0.5rem" }}>
                                {normalizedRole && (
                                    <button
                                        className="slideshow-button"
                                        onClick={() => saveTalk(targetStatus)}
                                        disabled={saving}
                                    >
                                        Save
                                    </button>
                                )}
                                {talkId && (
                                    <button
                                        onClick={deleteTalk}
                                        className="slideshow-button btn-danger"
                                    >
                                        Delete Talk
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ROW 2: Author + Status */}
                        {normalizedRole && (
                            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
                                <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                                    <span>Author:</span>
                                    <select
                                        value={talk.authorId || ""}
                                        onChange={(e) => setTalk((t) => ({ ...t, authorId: e.target.value }))}
                                        disabled={usersLoading}
                                        className="status-selector"
                                    >
                                        <option value="">— Select Author —</option>
                                        {users.map((u) => (
                                            <option key={u.id} value={u.id}>{u.label}</option>
                                        ))}
                                    </select>
                                </label>

                                <label style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                                    <span>Status:</span>
                                    <select
                                        className="status-selector"
                                        value={targetStatus}
                                        onChange={(e) => setTargetStatus(e.target.value)}
                                    >
                                        {allowedStatuses.map((status) => (
                                            <option key={status} value={status}>
                                                {status}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        )}
                    </div>
                </div>

                {/* Handout Verse (optional) - separate card */}
                <div className="verse-card">
                    <div className="verse-card__label">Handout verse (optional)</div>
                    <textarea
                        className="verse-card__text"
                        placeholder="No need to add the quotation marks — we'll take care of that for you."
                        value={talk.verseText}
                        onChange={(e) => setTalk((t) => ({ ...t, verseText: e.target.value }))}
                        rows={3}
                    />
                    <div className="verse-card__meta">
                        <label className="verse-card__ref-label" htmlFor="verse-ref">Reference</label>
                        <input
                            id="verse-ref"
                            type="text"
                            className="verse-card__ref subscribe-input"
                            placeholder="e.g., 1 Peter 3:15"
                            value={talk.verseRef}
                            onChange={(e) => setTalk((t) => ({ ...t, verseRef: e.target.value }))}
                        />
                    </div>
                </div>

                <section className="editor-sections">
                    {talk.sections.length === 0 ? (
                        // EMPTY STATE WHEN THERE ARE NO SECTIONS
                        <div
                            style={{
                                textAlign: "center",
                                margin: "1.5rem 0",
                                padding: "1.5rem",
                                borderRadius: "10px",
                                border: "1px dashed rgba(192, 141, 59, 0.45)",
                                background: "rgba(241, 178, 73, 0.05)",
                            }}
                        >
                            <p style={{ marginBottom: "0.75rem", fontSize: "0.95rem" }}>
                                This talk does not have any sections yet.
                            </p>
                            <div
                                style={{
                                    display: "inline-flex",
                                    gap: "0.5rem",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                }}
                            >
                                <button onClick={() => addSection(null)}>Add First Section</button>
                                <button onClick={() => addDividerSection(null)}>Add Divider</button>
                                <button onClick={() => addImageSection(null)}>Add Image Slide</button>
                            </div>
                        </div>
                    ) : (
                        // NORMAL CASE: RENDER EXISTING SECTIONS
                        talk.sections.map((section, idx) => {
                            if (section.type === "divider") {
                                return (
                                    <>
                                        <div
                                            key={idx}
                                            className="section-card divider-section"
                                            style={{
                                                background: "rgba(241, 178, 73, 0.06)",
                                                border: "1px dashed rgba(192, 141, 59, 0.5)",
                                                padding: "0.75rem",
                                                borderRadius: "8px",
                                                marginBottom: "1rem",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.5rem",
                                                    marginBottom: "0.5rem",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: "0.85rem",
                                                        fontWeight: 600,
                                                        opacity: 0.8,
                                                    }}
                                                >
                                                    Section Divider
                                                </span>
                                                <div
                                                    style={{
                                                        marginLeft: "auto",
                                                        display: "flex",
                                                        gap: "0.25rem",
                                                    }}
                                                >
                                                    <button
                                                        onClick={() =>
                                                            moveSection(idx, Math.max(0, idx - 1))
                                                        }
                                                        disabled={idx === 0}
                                                    >
                                                        ↑
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            moveSection(
                                                                idx,
                                                                Math.min(
                                                                    talk.sections.length - 1,
                                                                    idx + 1
                                                                )
                                                            )
                                                        }
                                                        disabled={idx === talk.sections.length - 1}
                                                    >
                                                        ↓
                                                    </button>
                                                    <button
                                                        onClick={() => removeSection(idx)}
                                                        style={{ background: "#e74c3c", color: "#fff" }}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Section Title"
                                                value={section.header || ""}
                                                onChange={(e) =>
                                                    updateSection(idx, "header", e.target.value)
                                                }
                                                className="subscribe-input"
                                                style={{ width: "100%" }}
                                            />
                                        </div>
                                        <div style={{ textAlign: "center", margin: "1rem 0" }}>
                                            <button onClick={() => addSection(idx)}>Add Section</button>
                                            <button
                                                onClick={() => addDividerSection(idx)}
                                                style={{ marginLeft: "0.5rem" }}
                                            >
                                                Add Divider
                                            </button>
                                            <button
                                                onClick={() => addImageSection(idx)}
                                                style={{ marginLeft: "0.5rem" }}
                                            >
                                                Add Image Slide
                                            </button>
                                        </div>
                                    </>
                                );
                            }

                            if (section.type === "image") {
                                return (
                                    <>
                                        <ImageSlideEditor
                                            key={idx}
                                            section={section}
                                            index={idx}
                                            total={talk.sections.length}
                                            onChange={(i, key, val) => updateSection(i, key, val)}
                                            onRemove={(i) => removeSection(i)}
                                            onMoveUp={(i) => moveSection(i, Math.max(0, i - 1))}
                                            onMoveDown={(i) =>
                                                moveSection(
                                                    i,
                                                    Math.min(talk.sections.length - 1, i + 1)
                                                )
                                            }
                                        />
                                        <div style={{ textAlign: "center", margin: "1rem 0" }}>
                                            <button onClick={() => addSection(idx)}>Add Section</button>
                                            <button
                                                onClick={() => addDividerSection(idx)}
                                                style={{ marginLeft: "0.5rem" }}
                                            >
                                                Add Divider
                                            </button>
                                            <button
                                                onClick={() => addImageSection(idx)}
                                                style={{ marginLeft: "0.5rem" }}
                                            >
                                                Add Image Slide
                                            </button>
                                        </div>
                                    </>
                                );
                            }

                            // content sections
                            const displayIndex =
                                talk.sections
                                    .slice(0, idx + 1)
                                    .filter((s) => s.type !== "divider").length - 1;
                            const sectionKey = String(section.sectionId || idx);

                            return (
                                <>
                                    <SectionEditor
                                        key={idx}
                                        section={section}
                                        sectionType={section.type}
                                        index={idx}
                                        displayIndex={displayIndex}
                                        total={talk.sections.length}
                                        onChange={updateSection}
                                        onRemove={removeSection}
                                        onMoveUp={(i) => moveSection(i, i - 1)}
                                        onMoveDown={(i) => moveSection(i, i + 1)}
                                        onAddCommentToSection={handleAddCommentForSection}
                                        comments={sectionComments[sectionKey] || []}
                                        onDeleteComment={handleDeleteComment}
                                    />
                                    <div style={{ textAlign: "center", margin: "1rem 0" }}>
                                        <button onClick={() => addSection(idx)}>Add Section</button>
                                        <button
                                            onClick={() => addDividerSection(idx)}
                                            style={{ marginLeft: "0.5rem" }}
                                        >
                                            Add Divider
                                        </button>
                                        <button
                                            onClick={() => addImageSection(idx)}
                                            style={{ marginLeft: "0.5rem" }}
                                        >
                                            Add Image Slide
                                        </button>
                                    </div>
                                </>
                            );
                        })
                    )}
                </section>

                {/* References (immutable title) - final card */}
                <ReferencesEditor
                    value={talk.references}
                    bodies={talk.referencesBodies}
                    onChange={(first, rest) => setTalk((t) => ({ ...t, references: first, referencesBodies: rest }))}
                    helperText="Number your sources; hyperlink titles/sources, not raw URLs."
                />
            </main>

            {/* Comment modal */}
            {commentModal.open && (
                <div className="modal-backdrop comment-modal-backdrop">
                    <div className="modal comment-modal">
                        <h3 className="comment-modal__title">
                            Add comment for Section #{(commentModal.sectionIndex ?? 0) + 1}
                        </h3>
                        <p className="comment-modal__subtitle">
                            This comment will be saved with your name in the global comments database for this talk.
                        </p>
                        <textarea
                            className="comment-modal__textarea"
                            placeholder="Write your comment here…"
                            rows={4}
                            value={commentModal.text}
                            onChange={(e) =>
                                setCommentModal((cm) => ({ ...cm, text: e.target.value }))
                            }
                        />
                        <div className="comment-modal__actions">
                            <button
                                type="button"
                                className="slideshow-button btn-secondary"
                                onClick={handleCancelComment}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="slideshow-button"
                                disabled={!commentModal.text.trim()}
                                onClick={handleSubmitComment}
                            >
                                Add Comment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <GuidelinesModal
                isOpen={isGuidelinesOpen}
                onClose={() => setIsGuidelinesOpen(false)}
            />
            <Footer />
            {showSaveToast && (
                <div role="status" aria-live="polite" className="toast toast--success">
                    Saved
                </div>
            )}
        </>
    );
}